import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit2, Layers, Star } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Loader from "../../../components/Loader/Loader";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./PlanView.module.scss";

// Reusable Detail Row Component
const DetailRow = ({ label, value }) => (
  <div className={styles["admin-plan-view__detail-row"]}>
    <span className={styles["admin-plan-view__detail-label"]}>{label}</span>
    <span className={styles["admin-plan-view__detail-value"]}>{value}</span>
  </div>
);

const PlanView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    try {
      const res = await apiGet("catalog", `/plans/${id}`);
      if (res?.status === "success") {
        setPlan(res.data);
      }
    } catch {
      // Error handled by showing null state
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  if (loading) {
    return (
      <div className={styles["admin-plan-view__loading-state"]}>
        <Loader />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className={styles["admin-plan-view__error-state"]}>
        <p>Plan not found.</p>
      </div>
    );
  }

  return (
    <div className={styles["admin-plan-view__page"]}>
      {/* HEADER & ACTIONS */}
      <div className={styles["admin-plan-view__header"]}>
        <div className={styles["admin-plan-view__title-area"]}>
          <button
            className={styles["admin-plan-view__back-btn"]}
            onClick={() => navigate("/admin/plans")}
            aria-label="Go Back"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>

          <div className={styles["admin-plan-view__title-group"]}>
            <h2 className={styles["admin-plan-view__title"]}>Plan Details</h2>
            <p className={styles["admin-plan-view__subtitle"]}>
              Viewing details for <strong>{plan.planName}</strong>
            </p>
          </div>
        </div>

        <button
          className={styles["admin-plan-view__edit-btn"]}
          onClick={() => navigate(`/admin/plans/edit/${id}`)}
        >
          <Edit2 size={16} strokeWidth={2.5} />
          Edit Plan
        </button>
      </div>

      {/* PLAN DETAILS CARD */}
      <div className={styles["admin-plan-view__card"]}>
        <div className={styles["admin-plan-view__card-header"]}>
          <div className={styles["admin-plan-view__icon-wrap"]}>
            <Layers size={32} strokeWidth={2.5} />
          </div>

          <div className={styles["admin-plan-view__plan-title-wrap"]}>
            <h3>{plan.planName}</h3>
            {plan.isPopular && plan.isPopular !== "—" && (
              <span
                className={`${styles["admin-plan-view__badge"]} ${styles["admin-plan-view__badge--popular"]}`}
              >
                <Star size={12} strokeWidth={3} /> {plan.isPopular}
              </span>
            )}
          </div>
        </div>

        <div className={styles["admin-plan-view__details-list"]}>
          <DetailRow label="Plan ID" value={plan.id} />
          <DetailRow
            label="Price"
            value={`₹${Number(plan.price).toLocaleString("en-IN")}`}
          />
          <DetailRow
            label="Credits"
            value={Number(plan.credits).toLocaleString("en-IN")}
          />
          <DetailRow label="Slug" value={plan.slug || "—"} />
          <DetailRow label="Icon Name" value={plan.iconName || "—"} />

          <DetailRow
            label="Status"
            value={
              <span
                className={`${styles["admin-plan-view__badge"]} ${
                  plan.isActive
                    ? styles["admin-plan-view__badge--active"]
                    : styles["admin-plan-view__badge--inactive"]
                }`}
              >
                {plan.isActive ? "Active" : "Inactive"}
              </span>
            }
          />

          <DetailRow
            label="Deleted Status"
            value={
              <span
                className={`${styles["admin-plan-view__badge"]} ${
                  plan.isDeleted
                    ? styles["admin-plan-view__badge--deleted"]
                    : styles["admin-plan-view__badge--inactive"]
                }`}
              >
                {plan.isDeleted ? "Deleted" : "Not Deleted"}
              </span>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PlanView;
